// Single source of truth for assessment-category values.
//
// These strings are persisted in DynamoDB (the question item's `type`), echoed into the results
// file (ConversationAnalytics.Summary.Validations[q].type) and used to build DynamoDB sort keys
// (`conduct#true`). Renaming a VALUE therefore needs a data migration - rename the LABEL instead,
// which is why the two are kept apart here.

export const ASSESSMENT_TYPE = Object.freeze({
  QUALITY: 'Quality',
  COMPLIANCE: 'Compliance',
  CONDUCT: 'Conduct',
});

export const ASSESSMENT_TYPE_LABELS = Object.freeze({
  [ASSESSMENT_TYPE.QUALITY]: 'Quality',
  [ASSESSMENT_TYPE.COMPLIANCE]: 'Compliance',
  [ASSESSMENT_TYPE.CONDUCT]: 'Agent Behavior and Conduct',
});

// Short form for table cells and badges, where the full conduct label does not fit.
export const ASSESSMENT_TYPE_SHORT_LABELS = Object.freeze({
  [ASSESSMENT_TYPE.QUALITY]: 'Quality',
  [ASSESSMENT_TYPE.COMPLIANCE]: 'Compliance',
  [ASSESSMENT_TYPE.CONDUCT]: 'Conduct',
});

export const ASSESSMENT_TYPE_OPTIONS = Object.freeze([
  { value: ASSESSMENT_TYPE.QUALITY, label: ASSESSMENT_TYPE_LABELS[ASSESSMENT_TYPE.QUALITY] },
  { value: ASSESSMENT_TYPE.COMPLIANCE, label: ASSESSMENT_TYPE_LABELS[ASSESSMENT_TYPE.COMPLIANCE] },
  { value: ASSESSMENT_TYPE.CONDUCT, label: ASSESSMENT_TYPE_LABELS[ASSESSMENT_TYPE.CONDUCT] },
]);

// Compliance and Conduct are pass/fail only and carry no weighting; a critical failure of either
// fails the whole call. Quality is the only weighted category.
export const isPassFailType = (type) =>
  type === ASSESSMENT_TYPE.COMPLIANCE || type === ASSESSMENT_TYPE.CONDUCT;

export const isWeightedType = (type) => type === ASSESSMENT_TYPE.QUALITY;

// Pre-check steps apply to the two pass/fail categories. Quality is weighted and always assessed,
// so a question that might not apply has no meaning there - its weight is in the denominator either
// way. Compliance gains this only where the author opts in per question: an ungated compliance
// question is scored exactly as it always has been.
export const supportsApplicability = (type) =>
  type === ASSESSMENT_TYPE.CONDUCT || type === ASSESSMENT_TYPE.COMPLIANCE;

export const typeLabel = (type) => ASSESSMENT_TYPE_LABELS[type] || type || '';
export const typeShortLabel = (type) => ASSESSMENT_TYPE_SHORT_LABELS[type] || type || '';

// A verdict can be recorded without being scored either way:
//   notApplicable - the author's pre-check steps did not hold, so the question did not apply
//   unchecked     - the assessor could not determine it; needs a human
// Neither is a pass or a fail, and neither may be rendered green or red - the entire point is that
// a question which did not apply must not look like a pass.
export const NA_ANSWERS = Object.freeze(['n/a', 'na', 'n\\a', 'not applicable']);

// The assessor is told to answer the question as worded, but a question whose own criteria asks
// for an overall verdict pulls it into answering "pass"/"fail". A verdict means "met the question",
// so it maps to the question's success answer - on a negatively worded question (success "no")
// "pass" is "no". Mirrors normalise_answer in pca-validations.py and overide.py.
const YES_ANSWERS = ['yes', 'y', 'true'];
const NO_ANSWERS = ['no', 'n', 'false'];
const PASS_VERDICTS = ['pass', 'passed', 'met'];
const FAIL_VERDICTS = ['fail', 'failed', 'not met'];

// The success answer as 'yes' or 'no'; empty defaults to 'yes'.
export const normaliseSuccess = (success) => {
  const value = String(success ?? '').trim().toLowerCase();
  if (!value || YES_ANSWERS.includes(value) || PASS_VERDICTS.includes(value)) return 'yes';
  if (NO_ANSWERS.includes(value) || FAIL_VERDICTS.includes(value)) return 'no';
  return value;
};

// An answer as 'yes'/'no', reading verdict words against success. Anything unrecognised comes back
// lower-cased and unchanged, so N/A spellings keep their own handling.
export const normaliseAnswer = (answer, success) => {
  const value = String(answer ?? '').trim().toLowerCase();
  if (YES_ANSWERS.includes(value)) return 'yes';
  if (NO_ANSWERS.includes(value)) return 'no';
  const expected = normaliseSuccess(success);
  if (PASS_VERDICTS.includes(value)) return expected;
  if (FAIL_VERDICTS.includes(value)) return expected === 'yes' ? 'no' : 'yes';
  return value;
};

// A stored verdict's success answer and its answer, normalised as above.
export const successOf = (parsed) => normaliseSuccess(parsed?.success || parsed?.successCriteria);
export const answerOf = (parsed) => normaliseAnswer(parsed?.answer, successOf(parsed));

export const isNotApplicable = (parsed) =>
  parsed?.notApplicable === true ||
  parsed?.applicable === false ||
  NA_ANSWERS.includes(String(parsed?.answer ?? '').trim().toLowerCase());

export const isUnchecked = (parsed) =>
  parsed?.unchecked === true || parsed?.needsReview === true;

// True when the verdict was not scored either way, whichever of the two states applies.
export const isNotScored = (parsed) => isUnchecked(parsed) || isNotApplicable(parsed);

// A pre-check step is a CONDITION - something simply true or false of the call. Authors migrating
// existing rubric text paste marking rules instead ("if no NRV is mentioned, mark Yes and stop"),
// which inverts the check: the gate evaluates the rule rather than the situation, and a question
// whose subject clearly appeared gets skipped.
//
// Kept conservative, because this only warns. A missed instruction costs a bad gate; a false
// positive costs a dismissed warning, but too many of those and authors stop reading it.
const SCORING_INSTRUCTION_PATTERNS = [
  /\bmark\s+(it\s+)?(as\s+)?(yes|no|n\/?a)\b/i,   // "mark Yes", "mark it as no", "mark N/A"
  /\bis\s+an?\s+(pass|fail|n\/?a)\b/i,            // "the absence of an NRV offer is a PASS"
  /\b(do\s*not|don'?t)\s+proceed\b/i,
  /\bproceed\s+to\s+step\b/i,
  /\band\s+stop\b/i,                              // "mark Yes and stop"
  /\bstep\s+\d/i,                                 // cross-references another step
];

export const looksLikeScoringInstruction = (text) =>
  SCORING_INSTRUCTION_PATTERNS.some(pattern => pattern.test(String(text ?? '')));

// Pair each authored pre-check step with the gate's verdict on it, for display.
//
// The step TEXT lives on applicabilityConditions and the RESULT on applicabilitySteps; they are
// matched by 1-based step number rather than position, because the gate stops at the first step
// that does not hold and so may return fewer results than there are steps. Either side may be
// short, absent or malformed - a verdict written before this existed has neither.
export const GATE_STEP_RESULTS = Object.freeze({
  TRUE: 'true',
  FALSE: 'false',
  UNKNOWN: 'unknown',
});

export const gateSteps = (parsed) => {
  const texts = Array.isArray(parsed?.applicabilityConditions) ? parsed.applicabilityConditions : [];
  const results = Array.isArray(parsed?.applicabilitySteps) ? parsed.applicabilitySteps : [];
  if (!texts.length && !results.length) return [];

  const count = Math.max(texts.length, results.length);
  return Array.from({ length: count }, (_, i) => {
    const number = i + 1;
    const result = results.find(r => r?.step === number) || results[i] || {};
    const evidence = result.evidence && typeof result.evidence === 'object' ? result.evidence : {};
    return {
      number,
      text: texts[i] || '',
      // Absent means the gate never got this far - it stopped at an earlier step.
      result: result.result || null,
      reason: result.reason || '',
      agent: evidence.agent || '',
      customer: evidence.customer || '',
      // Set when a reviewer corrected this step. originalReason holds what the gate said, which is
      // kept so the correction can be seen for what it is rather than silently replacing the record.
      overriddenBy: result.overriddenBy || '',
      overriddenAt: result.overriddenAt || '',
      originalReason: result.originalReason || '',
    };
  });
};

// True when a call's scorecard actually asked a conduct question.
//
// Deliberately derived from the call's own verdicts rather than from Rating.Conduct, which defaults
// to true in pcaresults.py - so a `Rating.Conduct !== undefined` check would claim a conduct verdict
// on every reprocessed call, including ones where conduct was never assessed.
export const hasConductQuestions = (callData) =>
  verdictTypes(callData).includes(ASSESSMENT_TYPE.CONDUCT);

// The assessment type of each question verdict on a call; unreadable entries are skipped.
const verdictTypes = (callData) => {
  const validations = callData?.ConversationAnalytics?.Summary?.Validations;
  if (!validations) return [];

  return Object.entries(validations).flatMap(([key, value]) => {
    if (key === 'Scores') return [];
    try {
      return [JSON.parse(value)?.type];
    } catch (e) {
      return [];
    }
  });
};

// How a scorecard is scored. Yes/No and Legacy are derived from each question's responseType, as
// they always have been; only Compliance-only is stored, on the template header. A compliance-only
// scorecard holds only Compliance questions, answered yes/no.
export const SCORING_SYSTEM = Object.freeze({
  YES_NO: 'yes_no',
  LEGACY: 'legacy',
  COMPLIANCE: 'compliance',
});

// A saved scorecard's scoring system: the stored one when present, otherwise derived from its
// questions exactly as the editor always has - so scorecards saved before this existed load unchanged.
export const scoringSystemOf = (template) => {
  if (template?.scoringSystem === SCORING_SYSTEM.COMPLIANCE) return SCORING_SYSTEM.COMPLIANCE;
  const questions = template?.questions || [];
  if (questions.some((q) => q.responseType === 'yes_no')) return SCORING_SYSTEM.YES_NO;
  if (questions.some((q) => q.responseType === 'scored')) return SCORING_SYSTEM.LEGACY;
  return SCORING_SYSTEM.YES_NO;
};

// A question moved onto a compliance-only scorecard: a pass/fail Compliance check answered yes/no.
// Its QA weighting no longer counts - only Quality questions are weighted.
export const asComplianceQuestion = (question) => ({
  ...question,
  type: ASSESSMENT_TYPE.COMPLIANCE,
  responseType: 'yes_no',
});

// Compliance-only is a scoring system chosen on the scorecard. It has no QA score, so the scorers
// store QA_Score as null (N/A) - a QA scorecard that genuinely scores 0 keeps its numeric 0, and a
// call not yet scored defaults to 0. So a null QA score, and only that, means compliance-only.
export const isComplianceOnlyScorecard = (callData) =>
  callData?.ConversationAnalytics?.Rating?.QA_Score === null;

// The same for a list record, whose Result is the call's rating; the indexer also sets
// `complianceOnly` from the same null.
export const isComplianceOnlyCall = (call) =>
  call?.Result?.QA_Score === null || call?.complianceOnly === true;

// The QA score to show for a list record, or null when there is none.
export const qaScoreOf = (call) =>
  isComplianceOnlyCall(call) ? null : (call?.Result?.QA_Score ?? null);

// Rate a call Pass/Fail on compliance alone when it has no QA score to band on - a
// compliance-only scorecard, or no QA_Score at all. Same rule as the indexer's result# facet.
export const isJudgedOnCompliance = (call) =>
  isComplianceOnlyCall(call) || call?.Result?.QA_Score === undefined || call?.Result?.QA_Score === null;

// Tailwind scans source text, so a class built as `md:grid-cols-${n}` is never generated. Every
// class has to appear as a complete literal, which is what this map is for.
export const WEIGHTING_GRID_CLASS = Object.freeze({
  [ASSESSMENT_TYPE.QUALITY]: 'grid grid-cols-1 md:grid-cols-4 gap-4',
  // Three, not two: Compliance also renders a Priority select that Quality and Conduct do not.
  [ASSESSMENT_TYPE.COMPLIANCE]: 'grid grid-cols-1 md:grid-cols-3 gap-4',
  [ASSESSMENT_TYPE.CONDUCT]: 'grid grid-cols-1 md:grid-cols-2 gap-4',
});

export const weightingGridClass = (type) =>
  WEIGHTING_GRID_CLASS[type] || WEIGHTING_GRID_CLASS[ASSESSMENT_TYPE.QUALITY];

// Non-sales: a QA manager marked an unsuccessful call that should not be evaluated, so it can be
// left out of scores. Stored as ConversationAnalytics.NonSalesDetails; a call written before the
// flag existed has none and is a sales call. Independent of assessed - it locks nothing.
export const nonSalesDetailsOf = (callData) =>
  callData?.ConversationAnalytics?.NonSalesDetails || { nonSales: false };

export const isNonSalesCall = (callData) => nonSalesDetailsOf(callData).nonSales === true;
